import React, { useRef } from 'react';
import { Backdrop, Box, Grid, Theme, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import colors from '../../../../../styles/colors';
import AssignmentEditorList from './question-list/AssignmentEditorList';
import { Button } from '../../../common/button/Button';
import { Add, Cancel, CheckCircle, Search, Sync } from '@material-ui/icons';
import {
  AssignmentData,
  AssignmentEditState,
  AssignmentPassage,
  AssignmentPassageQuestion,
  AssignmentQuestionFormat,
  AssignmentQuestionType
} from '../state/assignment-edit.types';
import { useAppDispatch } from '../../../../../redux/hooks';
import moment from 'moment';
import AssignmentEditor from './question-list/AssignmentEditor';
import { show } from 'redux-modal/lib/actions';
import { MODAL_ASSIGN_USER_GENERATED_CONTENT } from '../../../../../constants/modals';
import {
  isAssignmentAssignable,
  isAssignmentValid
} from '../../../util/AssignmentValidation';
import { useResizeDetector } from 'react-resize-detector';
import QuestionRenderer from './question-list/question-renderer/QuestionRenderer';
import PassageRenderer from './question-list/question-renderer/PassageRenderer';
import { AssignmentActionsAsync } from '../state/assignment/assignment-actions.async';
import { AssignmentQuestionActions } from '../state/question/assignment-question.actions';
import LoaderBackDrop from '../../../common/spinner/LoaderBackDrop';

const useStyles = makeStyles((theme: Theme) => ({
  actions: {
    marginTop: '1rem'
  },
  editStatus: {
    position: 'absolute',
    top: 20,
    right: 30,
    color: colors.assignment.lightgrey,
    fontWeight: 'bold'
  },
  center: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center'
  },
  actionContainer: {
    background: 'white',
    bottom: 0,
    zIndex: 1000,
    position: 'fixed',
    transform: 'translate(-3%)'
  },
  spinAnimation: {
    animation: '$spin 1s linear infinite'
  },
  '@keyframes spin': {
    '0%': {
      opacity: 1,
      transform: 'rotate(0deg)'
    },
    '50%': {
      transform: 'rotate(180deg)'
    },
    '100%': {
      opacity: 1,
      transform: 'rotate(360deg)'
    }
  }
}));

interface AssignmentEditorContainerProps {
  dashboardUserId: number;
  isEditMode: boolean;
  assignment: AssignmentEditState;
  originalAssignment: AssignmentEditState;
  userClasses: any[];
  handleQuestionBank(): void;
  showActions?: boolean;
}

const AssignmentEditorContainer: React.FC<AssignmentEditorContainerProps> = (
  props: AssignmentEditorContainerProps
) => {
  const classes = useStyles();

  const {
    assignment,
    dashboardUserId,
    isEditMode,
    userClasses,
    handleQuestionBank,
    showActions,
    originalAssignment
  } = props;
  const dispatch = useAppDispatch();
  const bottomScreenRef = useRef<HTMLInputElement>(null);
  /**
   * Resize's actions
   */
  const { width, ref } = useResizeDetector();
  let calculatedWidth = width;
  if (calculatedWidth) {
    calculatedWidth += (calculatedWidth / 100) * 4.5;
  }

  const handleAddQuestion = (rootId?: string, scrollDown = false) => {
    dispatch(AssignmentQuestionActions.addQuestion(rootId));
    if (bottomScreenRef && scrollDown) {
      setTimeout(() => {
        //@ts-ignore
        bottomScreenRef?.current?.scrollIntoView({
          block: 'end',
          inline: 'end',
          behavior: 'smooth'
        });
      });
    }
  };

  const handleAssign = () => {
    dispatch(
      show('modal', {
        type: MODAL_ASSIGN_USER_GENERATED_CONTENT,
        assignment,
        userClasses,
        dashboardUserId,
        assignmentId: assignment.id,
        isFromEdit: true
      })
    );
  };

  const handleUpdateQuestionType = (
    id: string,
    type: AssignmentQuestionType
  ) => {
    dispatch(AssignmentQuestionActions.updateQuestionType(id, type));
  };

  const handleDeleteQuestion = (id: string, rootId?: string) => {
    dispatch(AssignmentQuestionActions.deleteQuestion(id, rootId));
  };

  const handleQuestionText = (
    id: string,
    data: Partial<AssignmentData>,
    rootId?: string
  ) => {
    dispatch(AssignmentQuestionActions.updateQuestionText(id, data, rootId));
  };

  const handleQuestionFormat = (
    id: string,
    format: number,
    rootId?: string
  ) => {
    dispatch(
      AssignmentQuestionActions.updateQuestionFormat(id, format, rootId)
    );
  };

  const handleAnswerUpdate = (
    id: string,
    answerId: string,
    data: object,
    type?: AssignmentQuestionType,
    rootId?: string
  ) => {
    dispatch(
      AssignmentQuestionActions.updateAnswer(id, answerId, data, type, rootId)
    );
  };

  const handleDeleteAnswer = (
    id: string,
    answerId: string,
    type?: AssignmentQuestionType,
    rootId?: string
  ) => {
    dispatch(
      AssignmentQuestionActions.deleteAnswer(id, answerId, type, rootId)
    );
  };

  const handleAddAnswer = (id: string, rootId?: string) => {
    dispatch(AssignmentQuestionActions.addAnswer(id, rootId));
  };

  const setQuestionEditMode = (id: string, rootId?: string) => {
    dispatch(AssignmentQuestionActions.setToEditMode(id, rootId));
  };

  const handleQuestionIndex = (id: string, rootId?: string) => {
    const assignmentData = assignment.data;
    if (rootId) {
      const passage = assignmentData.find(
        e => e.id === rootId
      ) as AssignmentPassage;
      return passage?.questions?.findIndex(e => e.id === id) + 1 || 0;
    } else {
      const questionType = assignmentData.find(e => e.id === id)?.type;
      const ofType = assignmentData.filter(e => e.type === questionType);
      return ofType.findIndex(e => e.id === id) + 1;
    }
  };
  const isViewMode = (assignmentData: any) => {
    return (
      assignmentData.isFromQuestionBank ||
      (!assignmentData.isEditMode && assignmentData.type !== null) ||
      (!assignmentData.isUserGenerated && assignmentData.type !== null)
    );
  };
  return (
    <Grid container>
      <LoaderBackDrop
        loaderMessage={'saving'}
        loading={assignment.edit?.saving || false}
      />
      {assignment.data.length > 0 && (
        <Grid item xs={12}>
          <AssignmentEditorList>
            {assignment.data.map((assignmentData: any, index: number) => {
              if (isViewMode(assignmentData)) {
                const isEditable = isEditMode && assignmentData.isUserGenerated;
                const showDelete = isEditMode;
                return (
                  <Grid key={index} item xs={12}>
                    {assignmentData.type ===
                    AssignmentQuestionType.Independent ? (
                      <QuestionRenderer
                        isEditable={isEditable}
                        showDelete={showDelete}
                        setQuestionEditMode={setQuestionEditMode}
                        handleQuestionIndex={handleQuestionIndex}
                        handleDeleteQuestion={handleDeleteQuestion}
                        question={assignmentData}
                      />
                    ) : (
                      <PassageRenderer
                        isEditable={isEditable}
                        showDelete={showDelete}
                        setQuestionEditMode={setQuestionEditMode}
                        handleDeleteQuestion={handleDeleteQuestion}
                        passageData={assignmentData}
                        handleQuestionIndex={handleQuestionIndex}
                      />
                    )}
                  </Grid>
                );
              }
              return (
                <Grid key={assignmentData.id} item xs={12}>
                  <AssignmentEditor
                    handleQuestionText={handleQuestionText}
                    handleQuestionFormat={handleQuestionFormat}
                    handleUpdateQuestionType={handleUpdateQuestionType}
                    handleAnswerUpdate={handleAnswerUpdate}
                    handleDeleteAnswer={handleDeleteAnswer}
                    handleAddAnswer={handleAddAnswer}
                    handleAddQuestion={handleAddQuestion}
                    handleQuestionIndex={handleQuestionIndex}
                    handleDeleteQuestion={handleDeleteQuestion}
                    isEditMode={isEditMode}
                    data={assignmentData}
                    status={assignment.status}
                  />
                </Grid>
              );
            })}
          </AssignmentEditorList>
        </Grid>
      )}
      {showActions && (
        <Grid item xs={12} ref={ref}>
          <Grid container style={{ width: calculatedWidth }}>
            <Box width={'100%'}>
              {isEditMode && (
                <Grid item xs={12} className={classes.actions}>
                  <Grid
                    container
                    justify={'center'}
                    spacing={2}
                    alignItems={'center'}
                  >
                    <Grid item>
                      <Button
                        color={'green'}
                        variant={'filled'}
                        textColor={'white'}
                        style={{ padding: '.5rem .8rem' }}
                        icon={Add}
                        onClick={() => handleAddQuestion(undefined, true)}
                      >
                        Create question
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        color={colors.assignment.blue}
                        variant={'filled'}
                        textColor={'white'}
                        disabled={false}
                        onClick={handleQuestionBank}
                        style={{ padding: '.5rem .8rem' }}
                        icon={Search}
                      >
                        Explore question bank
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Box>
          </Grid>
        </Grid>
      )}

      {/*
       Used by scroll to smooth scroll to bottom
      */}
      <div
        style={{ position: 'absolute', height: '1px', bottom: -1000 }}
        ref={bottomScreenRef}
      />
    </Grid>
  );
};

export default AssignmentEditorContainer;
